import { createTheme } from '@mui/material/styles';
import { MixThemeOptions, MixTheme } from './ThemeOptions';

export const theme: MixTheme = createTheme({
    typography: {
        fontFamily: '"Sneak", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        h1: {
            fontSize: '1.875rem',
            fontWeight: 700,
            letterSpacing: '-0.025em',
        },
        h2: {
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.02em',
        },
        h3: {
            fontSize: '1.25rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
        },
        body1: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.8125rem',
            lineHeight: 1.4,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px 0 rgba(0,0,0,0.06)',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    transition: 'transform 80ms ease, box-shadow 80ms ease',
                    '&:active': {
                        transform: 'scale(0.97)',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
                    '&:not(.MuiInputBase-multiline)': {
                        minHeight: '44px',
                    },
                    '&.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)': {
                        minHeight: '42px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#DDE6F0',
                        borderWidth: '1px',
                    },
                    '&:hover': {
                        backgroundColor: '#FFFFFF',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#AEBBCD',
                    },
                    '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 0 0 2px rgba(79, 70, 229, 0.10)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4F46E5',
                        borderWidth: '1px',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#EF4444',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#F8FAFC',
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E2E8F0',
                    },
                    '& input': {
                        fontSize: '14px',
                        fontWeight: 550,
                        color: '#0F172A',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                    },
                    '& textarea': {
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#0F172A',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: '#64748B',
                    fontSize: '13px',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    '&.Mui-focused': {
                        color: '#4F46E5',
                    },
                    '&.Mui-error': {
                        color: '#DC2626',
                    },
                    '&.MuiInputLabel-shrink': {
                        backgroundColor: '#FFFFFF',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        borderRadius: '6px',
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: '2px',
                    marginTop: '4px',
                    fontSize: '11.5px',
                    lineHeight: 1.35,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#0F172A',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&.MuiIconButton-edgeEnd': {
                        marginRight: '-6px',
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '16px',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: '12px',
                },
            },
        },
    },
    palette: {
        primary: {
            main: '#4F46E5', // Modern premium Indigo
            light: '#818CF8',
            dark: '#3730A3',
        },
        secondary: {
            main: '#FFFFFF',
        },
        myAwesomeColor: '#E2E8F0', // Overridden to match design system border
        text: {
            primary: '#0F172A', // Slate 900 for dark readable headings and body
            secondary: '#475569', // Slate 600 for supporting labels
            disabled: '#94A3B8',
        },
        background: {
            default: '#F8FAFC', // Slate 50 for clean surface backgrounds
            paper: '#FFFFFF',
        },
        divider: '#E2E8F0',
    },
    font: {
        primary: 'Sneak',
        secondary: 'Sneak',
        tertiary: 'Sneak',
        sneak: 'Sneak',
    },
    global: {
        background: '#F8FAFC',
        primary: '#4F46E5',
        danger: '#EF4444',
        mid: '#F59E0B',
        success: '#10B981',
    },
} as MixThemeOptions);
